import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Check, AlertCircle, Crown } from 'lucide-react';
import { motion } from 'framer-motion';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const attemptsRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const data = await api.getPaymentStatus(sessionId);
        setPaymentInfo(data);

        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }

        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setStatus('failed');
          return;
        }

        if (attemptsRef.current >= 10) {
          setStatus('timeout');
          return;
        }

        timeoutRef.current = setTimeout(poll, 2000);
      } catch (error) {
        console.error('Poll error:', error);
        if (attemptsRef.current >= 10) {
          setStatus('timeout');
        } else {
          timeoutRef.current = setTimeout(poll, 2000);
        }
      }
    };

    poll();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #D1FAE5 0%, #FEF3C7 100%)' }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
        <Card className="p-8 rounded-3xl shadow-lg border-2 border-slate-200 text-center" data-testid="payment-result-card">
          {status === 'polling' && (
            <div data-testid="payment-polling">
              <Loader2 className="w-20 h-20 animate-spin text-emerald-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-slate-800 mb-2">Confirming your payment...</h1>
              <p className="text-slate-600">This will only take a moment.</p>
            </div>
          )}

          {status === 'paid' && (
            <div data-testid="payment-success">
              <img
                src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
                alt="Prof. Scaly"
                className="w-32 h-32 mx-auto mb-4 object-contain"
              />
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="bg-green-500 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4"
              >
                <Check className="w-12 h-12 text-white" strokeWidth={3} />
              </motion.div>
              <h1 className="text-3xl font-heading font-bold text-emerald-700 mb-2" data-testid="payment-success-title">Welcome to Premium! 🎉</h1>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-bold my-3">
                <Crown className="w-4 h-4" strokeWidth={2.5} />
                {paymentInfo?.level} Subscription Active
              </div>
              <p className="text-slate-600 mb-6">You now have unlimited AI-generated practice for a full year. Time to master PSLE English!</p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                data-testid="success-go-to-dashboard"
              >
                Start Practicing
              </Button>
            </div>
          )}

          {status === 'failed' && (
            <div data-testid="payment-failed">
              <div className="bg-red-100 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-12 h-12 text-red-600" strokeWidth={2.5} />
              </div>
              <h1 className="text-2xl font-heading font-bold text-red-700 mb-2">Payment Failed</h1>
              <p className="text-slate-600 mb-6">Something went wrong. Please try again or use a different payment method.</p>
              <Button
                onClick={() => navigate('/subscription')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold"
              >
                Try Again
              </Button>
            </div>
          )}

          {status === 'timeout' && (
            <div data-testid="payment-timeout">
              <Loader2 className="w-20 h-20 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold text-slate-800 mb-2">Still processing...</h1>
              <p className="text-slate-600 mb-6">Your payment is taking longer than usual. It should be confirmed shortly — check your subscription page in a minute.</p>
              <Button
                onClick={() => navigate('/subscription')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold"
              >
                Check Subscription Status
              </Button>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
